import { roleLabel, type WfT } from '@/features/workflow/shared'

/** One AU account and its Aufmass access (GET /api/users/). */
export interface DirectoryUser {
  userId: string
  username: string | null
  name: string | null
  surname: string | null
  email: string | null
  loginType: number
  companyId: string | null
  company: string | null
  employeeId: string | null
  employeeName: string | null
  active: boolean
  createdAt: string | null
  /** Has an Aufmass record: signed in once, or an admin set them up. */
  provisioned: boolean
  isAdmin: boolean
  canEdit: boolean
  pages: string[]
  roles: string[]
  /** Direct pages plus every page their roles open. */
  effectivePages: string[]
  lastLogin: string | null
  suggestedRoles: string[]
  denied: boolean
}

/** A role and the modules it opens (GET /api/users/roles). */
export interface AccessRole {
  role: string
  label: string
  description: string | null
  pages: string[]
  canEdit: boolean
  builtin: boolean
  holders: number
}

export interface AuOptions {
  enabled: boolean
  companies: { id: string; name: string; isSub: boolean }[]
  loginTypes: number[]
  privileged: number[]
  passwordLength: number
}

export interface EmployeeHit { id: string; name: string; company: string | null; hasLogin: boolean }

/** AU's own names for its login types: proper nouns from AU, shown as they are there. */
export const LOGIN_TYPES: Record<number, string> = {
  0: 'Administrator', 1: 'Projektleiter', 2: 'CEO', 3: 'VIP CEO', 4: 'Accounting', 5: 'HR', 6: 'MIH Data',
  7: 'Rollout Manager', 10: 'Image Reviewer', 12: 'Kolonnenführer', 13: 'Projektleiter Spülbohrung',
  14: 'Oberbauleiter', 15: 'VIP HR+', 71: 'Translation only', 90: 'SaaS', 97: 'External',
}
export const loginTypeLabel = (t: number) => LOGIN_TYPES[t] ?? `#${t}`

export const fullName = (u: Pick<DirectoryUser, 'name' | 'surname' | 'username'>) =>
  [u.name, u.surname].filter(Boolean).join(' ').trim() || u.username || ''

/** A built-in role in the reader's language; a custom role by the name an admin gave it. */
export function roleName(tw: WfT, role: string, catalog?: AccessRole[]) {
  const r = catalog?.find((x) => x.role === role)
  return r && !r.builtin ? r.label : roleLabel(tw, role)
}

/** Lowercase ASCII first.last from a name, the way most AU usernames look. */
export function suggestUsername(name: string, surname: string) {
  const clean = (s: string) => s.trim().toLowerCase()
    .replace(/ß/g, 'ss').replace(/đ/g, 'dj').replace(/[äæ]/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  return [clean(name), clean(surname)].filter(Boolean).join('.')
}
